import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './core/stores/authStore'

// Pages
import LoginPage from './pages/LoginPage'
import SuperAdminLayout from './pages/superadmin/SuperAdminLayout'
import TenantsPage from './pages/superadmin/TenantsPage'
import ModulesPage from './pages/superadmin/ModulesPage'
import TenantDetailPage from './pages/superadmin/TenantDetailPage'

// Tenant Pages
import TenantLayout from './pages/tenant/TenantLayout'
import DashboardPage from './pages/tenant/DashboardPage'
import UtentiPage from './pages/tenant/anagrafiche/UtentiPage'
import ClientiPage from './pages/tenant/anagrafiche/ClientiPage'
import ListinoPage from './pages/tenant/anagrafiche/ListinoPage'
import PersonalePage from './pages/tenant/anagrafiche/PersonalePage'
import StrumentiPage from './pages/tenant/anagrafiche/StrumentiPage'

// Offerte Pages
import OffertePage from './pages/tenant/offerte/OffertePage'
import OffertaDetailPage from './pages/tenant/offerte/OffertaDetailPage'
import ConfigurazionePage from './pages/tenant/offerte/ConfigurazionePage'

// Configurazione Pages
import GeneralePage from './pages/tenant/configurazione/GeneralePage'
import ModelliStampaPage from './pages/tenant/configurazione/ModelliStampaPage'
import TemplateEditorPage from './pages/tenant/configurazione/TemplateEditorPage'
import SchedeDinamichePage from './pages/tenant/configurazione/SchedeDinamichePage'
import SchedaBuilderPage from './pages/tenant/configurazione/SchedaBuilderPage'

// Commesse Pages
import CommessePage from './pages/tenant/commesse/CommessePage'
import CommessaDetailPage from './pages/tenant/commesse/CommessaDetailPage'
import StrategiaPage from './pages/tenant/strategia/StrategiaPage'

// Scadenziario
import ScadenziarioPage from './pages/tenant/ScadenziarioPage'

// Calendario
import CalendarioPage from './pages/tenant/calendario/CalendarioPage'

// Fascicoli
import FascicoliPage from './pages/tenant/fascicoli/FascicoliPage'
import FascicoloDetailPage from './pages/tenant/fascicoli/FascicoloDetailPage'

// Approvazioni
import ApprovazioniPage from './pages/tenant/ApprovazioniPage'

// Riepiloghi
import RiepiloghiPage from './pages/tenant/RiepiloghiPage'

// Pagine pubbliche (senza login)
import SchedaPubblicaPage from './pages/public/SchedaPubblicaPage'

// Selezione azienda
import SelectAziendaPage from './pages/SelectAziendaPage'

// Rendicontazione Pages
// Rendicontazione ore integrata nelle commesse (non menu standalone)

// Magazzino Pages
import ArticoliPage from './pages/tenant/magazzino/ArticoliPage'
import FornitoriPage from './pages/tenant/magazzino/FornitoriPage'
import DistintaBasePage from './pages/tenant/magazzino/DistintaBasePage'
import MovimentiPage from './pages/tenant/magazzino/MovimentiPage'
import GiacenzePage from './pages/tenant/magazzino/GiacenzePage'

// Codice segreto per accesso SuperAdmin
export const SUPERADMIN_CODE = '78NIG26'

// Error Boundary per catturare errori di rendering React
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-2">Errore Applicazione</h2>
            <p className="text-gray-600 mb-4">Si e' verificato un errore imprevisto.</p>
            <pre className="text-xs text-left bg-gray-50 p-3 rounded mb-4 overflow-auto max-h-32 text-red-500">
              {this.state.error?.message}
            </pre>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Torna alla Home
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Protected route wrapper
function ProtectedRoute({
  children,
  requiredType
}: {
  children: React.ReactNode
  requiredType?: 'superadmin' | 'tenant_user'
}) {
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated) {
    // Redirect alla login appropriata
    if (requiredType === 'superadmin') {
      return <Navigate to={`/${SUPERADMIN_CODE}`} replace />
    }
    return <Navigate to="/" replace />
  }

  if (requiredType && user?.user_type !== requiredType) {
    if (user?.user_type === 'superadmin') {
      return <Navigate to="/superadmin" replace />
    }
    return <Navigate to="/app" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Login diretta — tenant fisso "cas" */}
        <Route path="/" element={<LoginPage />} />

        {/* Pagina pubblica: compilazione scheda esterna */}
        <Route path="/public/scheda/:tenantCode/:token" element={<SchedaPubblicaPage />} />

        {/* Selezione azienda post-login */}
        <Route path="/select-azienda" element={<SelectAziendaPage />} />

        {/* Login SuperAdmin (codice segreto) */}
        <Route path="/:code" element={<LoginPage />} />

        {/* SuperAdmin routes */}
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute requiredType="superadmin">
              <SuperAdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/superadmin/tenants" replace />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="modules" element={<ModulesPage />} />
        </Route>

        {/* Tenant routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute requiredType="tenant_user">
              <TenantLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="anagrafiche/utenti" element={<UtentiPage />} />
          <Route path="anagrafiche/clienti" element={<ClientiPage />} />
          <Route path="anagrafiche/listino" element={<ListinoPage />} />
          <Route path="anagrafiche/personale" element={<PersonalePage />} />
          <Route path="anagrafiche/strumenti" element={<StrumentiPage />} />
          <Route path="offerte" element={<OffertePage />} />
          <Route path="offerte/:id" element={<OffertaDetailPage />} />
          <Route path="approvazioni" element={<ApprovazioniPage />} />
          <Route path="commesse" element={<CommessePage />} />
          <Route path="commesse/:id" element={<CommessaDetailPage />} />
          <Route path="scadenziario" element={<ScadenziarioPage />} />
          <Route path="calendario" element={<CalendarioPage />} />
          <Route path="fascicoli" element={<FascicoliPage />} />
          <Route path="fascicoli/:id" element={<FascicoloDetailPage />} />
          <Route path="riepiloghi" element={<RiepiloghiPage />} />
          <Route path="strategia" element={<StrategiaPage />} />
          <Route path="magazzino/articoli" element={<ArticoliPage />} />
          <Route path="magazzino/fornitori" element={<FornitoriPage />} />
          <Route path="magazzino/distinta-base" element={<DistintaBasePage />} />
          <Route path="magazzino/movimenti" element={<MovimentiPage />} />
          <Route path="magazzino/giacenze" element={<GiacenzePage />} />
          <Route path="configurazione" element={<Navigate to="configurazione/generale" replace />} />
          <Route path="configurazione/generale" element={<GeneralePage />} />
          <Route path="configurazione/tariffe" element={<ConfigurazionePage />} />
          <Route path="configurazione/modelli-stampa" element={<ModelliStampaPage />} />
          <Route path="configurazione/modelli-stampa/:templateId/editor" element={<TemplateEditorPage />} />
          <Route path="configurazione/schede-dinamiche" element={<SchedeDinamichePage />} />
          <Route path="configurazione/schede-dinamiche/:templateId/builder" element={<SchedaBuilderPage />} />
        </Route>

        {/* Catch all - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}
